import type { ConversationSubscribeResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf, DatagramErrorTypeBuf } from "~/classes/generated/datagram"
import { ConversationSubscribeRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addConversationIdToBuf, parseConversationId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("conversation/subscribe")

export function sendConversationSubscribeRequests(requests: ConversationSubscribeRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)
  const subscribeRequests = []
  for (const request of requests) {
    const requestId = addRequestIdToBuf(fbb, request.requestId)
    const conversationId = addConversationIdToBuf(fbb, request.conversationId)
    ConversationSubscribeRequestDatagramBuf.startConversationSubscribeRequestDatagramBuf(fbb)
    ConversationSubscribeRequestDatagramBuf.addRequestId(fbb, requestId)
    ConversationSubscribeRequestDatagramBuf.addOffline(fbb, request.offline)
    ConversationSubscribeRequestDatagramBuf.addConversationId(fbb, conversationId)
    const subscribeRequest = ConversationSubscribeRequestDatagramBuf.endConversationSubscribeRequestDatagramBuf(fbb)

    RequestDatagramBuf.startRequestDatagramBuf(fbb)
    RequestDatagramBuf.addConversationSubscribeRequest(fbb, subscribeRequest)
    subscribeRequests.push(RequestDatagramBuf.endRequestDatagramBuf(fbb))
  }

  const subscribe = DatagramBuf.createRequestVector(fbb, subscribeRequests)
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, subscribe)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleConversationSubscribeResponseDatagramBuf(datagram: ConversationSubscribeResponseDatagramBuf) {
  onConversationSubscribeResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    offline: datagram.offline(),
    conversationId: parseConversationId(datagram.conversationId()!),
  })
}

export function onConversationSubscribeResponse(response: ConversationSubscribeResponseDatagram) {
  const { emitInterfaceEvent } = useEvents()

  console.debug("Subscribe response", clone(response))

  emitInterfaceEvent("conversationSubscribeResponse", response, response.requestId)

  if (response.error)
    return onConversationSubscribeErrored(response.error)
}

function onConversationSubscribeErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  if (enumEq(error.type, DatagramErrorTypeBuf.IgnoredDuplicateAction))
    return console.warn("Already subscribed with that subscription id")

  console.error("Subscription error", error)
  return notifyDatagramError(error, "Could not subscribe to updates, your browser might not be up to date")
}
